import React from "react";
import { Button, Tooltip } from "antd";
import { DownloadOutlined, ExportOutlined, FileImageOutlined, FilePdfOutlined } from "@ant-design/icons";

import config from "../../../../config";

/**
 * A readable, full-size PDF preview for an estimate's attached document.
 *
 * Why this exists rather than reusing `PredeterminationUploadedFiles`: that
 * component is a 200x133 THUMBNAIL — it sets `pointerEvents: "none"` on the
 * iframe and wraps the whole thing in an `<a target="_blank">`, so the document
 * is decorative and the only way to actually read it is to leave the app. For a
 * "Simple Upload" estimate the filed PDF *is* the entire content of the record,
 * so it needs to be legible in place.
 *
 * Dropping those two things hands scroll / zoom / page-nav / print to the
 * browser's built-in PDF viewer, which is why this needs no PDF library.
 *
 * `documentUrl` is root-relative (`/media/...`) because the serializer returns
 * `obj.document.file.url`, so it gets prefixed with the API gateway host — the
 * same convention `PredeterminationUploadedFiles` uses. Don't switch the backend
 * to `build_absolute_uri`; it would double-prefix every existing thumbnail.
 *
 * `href` is the other way in: a URL used as-is. The office's claim forms are
 * PHI served through an access-checked endpoint, so ProtectedFilePreview fetches
 * them with the auth header and hands over an object URL — and a form may be a
 * photo, hence `kind` ("pdf" | "image").
 */
export default function EstimatePdfPreview({ documentUrl, documentName, height = 640, href: hrefProp, kind = "pdf" }) {
  const href = hrefProp || (documentUrl ? config.apiGateway.URL + documentUrl : null);
  if (!href) {
    return (
      <div
        style={{
          border: "1px dashed #e8e0f5",
          borderRadius: 8,
          padding: "28px 16px",
          textAlign: "center",
          color: "#848696",
          fontSize: 13,
        }}
      >
        No file attached.
      </div>
    );
  }

  const name = documentName || (kind === "image" ? "claim-form" : "estimate.pdf");
  const isImage = kind === "image";

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minWidth: 0,
            color: "#3d2f5e",
            fontSize: 13,
          }}
        >
          {isImage ? <FileImageOutlined style={{ color: "#6B43B5" }} /> : <FilePdfOutlined style={{ color: "#6B43B5" }} />}
          <span
            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            title={name}
          >
            {name}
          </span>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <Tooltip title="Open in a new tab">
            <Button
              size="small"
              icon={<ExportOutlined />}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open
            </Button>
          </Tooltip>
          <Tooltip title={isImage ? "Download the image" : "Download the PDF"}>
            <Button size="small" icon={<DownloadOutlined />} href={href} download={name}>
              Download
            </Button>
          </Tooltip>
        </div>
      </div>

      {isImage ? (
        <img
          src={href}
          alt={name}
          style={{
            width: "100%",
            maxHeight: height,
            objectFit: "contain",
            border: "1px solid #e8e0f5",
            borderRadius: 8,
            background: "#fbfaff",
            display: "block",
          }}
        />
      ) : (
        <iframe
          // #view=FitH fits the page width on load. Chrome/Adobe honour it; other
          // viewers ignore it harmlessly.
          src={`${href}#view=FitH`}
          title={name}
          style={{
            width: "100%",
            height,
            border: "1px solid #e8e0f5",
            borderRadius: 8,
            background: "#fbfaff",
            display: "block",
          }}
        />
      )}
    </div>
  );
}
